import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { chatbotApi } from '../services/api';
import { Chatbot } from '../types';
import { toast } from 'react-hot-toast';
import { clsx } from 'clsx';

export default function Chatbots() {
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadChatbots();
  }, []);

  // A chatbot whose knowledge base is still provisioning sits at 'creating' for
  // up to ~3 minutes. Without this the row never changes on its own and the
  // user has to guess when to refresh. Polls only while something is actually
  // creating, and stops as soon as nothing is — so a settled list makes no
  // requests at all.
  // Depends on the boolean, not on `chatbots` itself: every poll replaces the
  // array, which would tear down and rebuild the interval on each tick.
  // Also polls while a knowledge base is still being read: a crawl finishes minutes
  // after creation does, and without this the card would sit on "Reading sources..."
  // until the user reloaded by hand.
  const hasCreating = chatbots.some((c) => c.status === 'creating' || c.indexing);
  useEffect(() => {
    if (!hasCreating) return;
    const timer = setInterval(() => loadChatbots({ silent: true }), 5000);
    return () => clearInterval(timer);
  }, [hasCreating]);

  const loadChatbots = async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      // A background poll must not flip the page back to its loading skeleton.
      if (!silent) setLoading(true);
      const response = await chatbotApi.getChatbots();
      if (response.success) {
        setChatbots(response.data || []);
      } else if (!silent) {
        toast.error('Failed to load chatbots');
      }
    } catch (error) {
      console.error('Error loading chatbots:', error);
      // Silent polls stay silent on failure too — otherwise a transient network
      // problem raises a toast every 5 seconds until provisioning finishes.
      if (!silent) toast.error('Failed to load chatbots');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleDelete = async (chatbotId: string) => {
    if (!window.confirm('Are you sure you want to delete this chatbot? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(chatbotId);
      const response = await chatbotApi.deleteChatbot(chatbotId);
      if (response.success) {
        setChatbots(prev => prev.filter(bot => bot.chatbotId !== chatbotId));
        toast.success('Chatbot deleted successfully');
      } else {
        toast.error('Failed to delete chatbot');
      }
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      toast.error('Failed to delete chatbot');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSuspend = async (chatbot: Chatbot) => {
    try {
      const response = await chatbotApi.updateChatbot(chatbot.chatbotId, {
        suspend: !chatbot.suspend
      });
      if (response.success) {
        setChatbots(prev => prev.map(bot => 
          bot.chatbotId === chatbot.chatbotId 
            ? { ...bot, suspend: !bot.suspend }
            : bot
        ));
        toast.success(chatbot.suspend ? 'Chatbot activated' : 'Chatbot suspended');
      } else {
        toast.error('Failed to update chatbot');
      }
    } catch (error) {
      console.error('Error updating chatbot:', error);
      toast.error('Failed to update chatbot');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
            Chatbots
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your chatbots and their configurations
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <Link
            to="/chatbots/new"
            className="btn-primary"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            New Chatbot
          </Link>
        </div>
      </div>

      {/* Chatbots List */}
      {chatbots.length === 0 ? (
        <div className="text-center bg-white dark:bg-slate-800 shadow-sm rounded-2xl border border-gray-100 dark:border-slate-700 py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No chatbots</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new chatbot.</p>
          <div className="mt-6">
            <Link
              to="/chatbots/new"
              className="btn-primary"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              New Chatbot
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 shadow-sm overflow-hidden rounded-2xl border border-gray-100 dark:border-slate-700">
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {chatbots.map((chatbot) => {
              const isCreating = chatbot.status === 'creating';
              const isFailed = chatbot.status === 'failed';
              // Indexing is a knowledge-source state, not a chatbot state, so it comes
              // from the list endpoint rather than from `status`. Shown only on a bot
              // that is otherwise usable: during creation it would be noise, since the
              // knowledge base does not exist yet.
              const isIndexing = !isCreating && !isFailed && chatbot.indexing;
              return (
              <li key={chatbot.chatbotId} className={clsx(
                "px-6 py-4",
                (isCreating || isFailed) && "opacity-60"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="flex-shrink-0">
                      <div className={clsx(
                        'h-3 w-3 rounded-full',
                        isCreating 
                          ? 'bg-primary-500 dark:bg-primary-400 animate-pulse'
                          : isFailed
                            ? 'bg-red-400 dark:bg-red-500'
                            : isIndexing
                              ? 'bg-primary-500 dark:bg-primary-400 animate-pulse'
                              : chatbot.suspend 
                                ? 'bg-orange-400 dark:bg-orange-500' 
                                : 'bg-green-400 dark:bg-green-500'
                      )}></div>
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <div className="flex items-center">
                        <Link
                          to={(isCreating || isFailed) ? '#' : `/chatbots/${chatbot.chatbotId}`}
                          className={clsx(
                            "text-sm font-medium truncate",
                            (isCreating || isFailed)
                              ? "text-gray-500 dark:text-gray-400 cursor-default" 
                              : "text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                          )}
                          onClick={(isCreating || isFailed) ? (e) => e.preventDefault() : undefined}
                        >
                          {chatbot.name}
                        </Link>
                        <span className={clsx(
                          'ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                          isCreating
                            ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300'
                            : isFailed
                              ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                              : isIndexing
                                ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300'
                                : chatbot.suspend
                                  ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300'
                                  : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                        )}>
                          {isCreating ? 'Creating...' : isFailed ? 'Failed' : isIndexing ? 'Reading sources...' : chatbot.suspend ? 'Paused' : 'Active'}
                        </span>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {isCreating 
                            ? 'Knowledge base is being provisioned...' 
                            : chatbot.description || 'No description'
                          }
                        </p>
                        <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <span>{chatbot.modelId}</span>
                          {isFailed && chatbot.message && (
                            <>
                              <span className="mx-2">•</span>
                              <span className="text-red-600 dark:text-red-400" title={chatbot.message}>
                                {chatbot.message.length > 50 ? `${chatbot.message.substring(0, 50)}...` : chatbot.message}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleSuspend(chatbot)}
                      disabled={isCreating || isFailed}
                      className={clsx(
                        'inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
                        (isCreating || isFailed)
                          ? 'border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-slate-800 cursor-not-allowed opacity-50'
                          : chatbot.suspend
                            ? 'border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/50 hover:bg-green-100 dark:hover:bg-green-900/70 focus:ring-green-500'
                            : 'border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/50 hover:bg-orange-100 dark:hover:bg-orange-900/70 focus:ring-orange-500'
                      )}
                    >
                      {(isCreating || isFailed) ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                        </>
                      ) : chatbot.suspend ? (
                        <>
                          <PlayIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                          Activate
                        </>
                      ) : (
                        <>
                          <PauseIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                          Pause
                        </>
                      )}
                    </button>
                    <Link
                      to={(isCreating || isFailed) ? '#' : `/chatbots/${chatbot.chatbotId}/settings`}
                      className={clsx(
                        "inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800",
                        (isCreating || isFailed)
                          ? "border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-slate-800 cursor-not-allowed opacity-50"
                          : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 focus:ring-primary-500"
                      )}
                      onClick={(isCreating || isFailed) ? (e) => e.preventDefault() : undefined}
                    >
                      <PencilIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(chatbot.chatbotId)}
                      disabled={deletingId === chatbot.chatbotId || isCreating}
                      className={clsx(
                        "inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed",
                        isCreating
                          ? "border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-slate-800"
                          : "border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/50 hover:bg-red-100 dark:hover:bg-red-900/70 focus:ring-red-500"
                      )}
                    >
                      {deletingId === chatbot.chatbotId ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <>
                          <TrashIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}